import React from 'react';
import { KbSection, Callout, StepList, VisibleTo } from '../../components/knowledge/primitives.jsx';
import SidebarDemo from '../../components/knowledge/demos/SidebarDemo.jsx';
import PinLockDemo from '../../components/knowledge/demos/PinLockDemo.jsx';
import { ROLE_LABELS } from '../../auth/roles.js';

// "Getting started" articles. All four are visible to every role — the
// front-to-back onboarding path starts here. Two of them ('your-access',
// 'pin-lock') read the live user object, so the same article renders
// differently per tier without ever mentioning features the reader lacks.

function WelcomeBody({ user }) {
  return (
    <>
      <KbSection title="What the AE Tracker is">
        <p>
          The AE Tracker is the single place for recording and managing <strong>adverse
          events</strong>, <strong>near misses</strong> and <strong>clinic incidents</strong> —
          end to end, from the moment something happens in clinic to the day the case is
          closed. Everything about an event lives in one shared record with its full
          history — instead of email threads and spreadsheets.
        </p>
      </KbSection>

      <KbSection title="What a case is" delay={1}>
        <p>
          Every report becomes a <strong>case</strong>: one event, one record. A case starts
          with the facts — clinic, date of incident, staff member, event type — and gathers
          everything that happens while it is worked: comments, attachments, status changes
          and a full history. Each case gets its own <strong>AE reference</strong> (like
          AE-042) that everyone can quote.
        </p>
      </KbSection>

      <KbSection title="The life of a case" delay={2}>
        <p>
          A case moves through statuses as it is worked — for example
          <strong> New → Pending → Open → With Claims → Closed</strong> (a new case becomes
          Pending on the first action someone other than its reporter takes). Your admin may
          have configured different values, but the idea is always the same: the status badge
          tells you at a glance where a case stands, and the case history records every step
          it took to get there.
        </p>
      </KbSection>

      <KbSection title="Where things live" delay={3}>
        <StepList steps={[
          <><strong>Dashboard</strong> — the overview: trends, counts and charts across the cases you can see.</>,
          <><strong>All Cases</strong> — the full list, with search, filters and sorting. Click a row to open the case.</>,
          ...(user?.canSubmitCase ? [
            <><strong>New Submission</strong> — where every new report starts, whatever the event type.</>,
          ] : []),
          <><strong>Knowledge</strong> — this guide. It only shows articles for features your role can use, so it reads front to back as your personal manual.</>,
        ]} />
      </KbSection>
    </>
  );
}

function NavigationBody({ user }) {
  return (
    <>
      <KbSection title="The sidebar">
        <p>
          Everything is reached from the sidebar on the left. The <strong>Overview</strong> section
          holds <strong>Dashboard</strong>, <strong>All Cases</strong> and <strong>Knowledge</strong>;
          the page you are on is highlighted.
          <VisibleTo user={user} when={(u) => !!u?.canSubmitCase}>
            {' '}Below it, the <strong>Workspace</strong> section holds <strong>New Submission</strong> —
            the starting point for every new report.
          </VisibleTo>
        </p>
        <SidebarDemo />
      </KbSection>

      <KbSection title="Making room" delay={1}>
        <p>
          On desktop, the small arrow on the sidebar's edge shrinks it to icons only —
          handy when you want more room for the case table. Point at an icon and its name
          pops up. The app remembers your choice on this device, so the sidebar stays the
          way you left it next time.
        </p>
        <p>
          On a phone or narrow window, the sidebar becomes a drawer instead: the top bar
          shows a menu button that slides it in over the page. It closes when you pick a
          page, tap outside it, or use its close button.
        </p>
      </KbSection>

      <KbSection title="The user menu" delay={2}>
        <p>
          Your name, photo and role sit at the bottom of the sidebar. Click them to open the
          user menu, which holds <strong>What’s new</strong> (this release's highlights),{' '}
          <strong>Lock now</strong> (lock the screen immediately), <strong>Change PIN</strong>,{' '}
          <strong>Privacy</strong> (frost photo previews — it has its own article) and{' '}
          <strong>Sign out</strong>.
          <VisibleTo user={user} when={(u) => !!u?.canManageAdminConfig}>
            {' '}As a Super Admin you also get <strong>Admin Panel</strong> here.
          </VisibleTo>
          <VisibleTo user={user} when={(u) => !!u?.canImpersonate}>
            {' '}<strong>Assume identity</strong> is in this menu too — preview the app as another user (nothing can be saved while assuming).
          </VisibleTo>
        </p>
        <Callout tone="info">
          <strong>Sign out</strong> ends your Microsoft session for the tracker. On a shared
          clinic machine, prefer it over just closing the tab.
        </Callout>
      </KbSection>

      <KbSection title="Keyboard basics" delay={3}>
        <StepList steps={[
          <>Prefer the keyboard? In <strong>All Cases</strong>, press <strong>Tab</strong> to reach a row, then <strong>Enter</strong> (or <strong>Space</strong>) to open the case.</>,
          <><strong>Escape</strong> closes pop-ups and file previews.</>,
        ]} />
      </KbSection>
    </>
  );
}

function YourAccessBody({ user }) {
  const roleLabel = ROLE_LABELS[user?.role] || user?.role || 'Unknown role';
  const abilities = [
    <>Open the <strong>Dashboard</strong> and browse, search and filter <strong>All Cases</strong>.</>,
    user?.canSubmitCase && <>Report new events through <strong>New Submission</strong>.</>,
    user?.canComment && <>Comment on cases.</>,
    user?.canResolveCase && <>Resolve a case when the work is done.</>,
    user?.canReopenCase && <>Reopen a resolved case.</>,
    user?.canDeleteCase && <>Delete a case.</>,
    user?.canSeeCosts && <>See the costs recorded against a case.</>,
    user?.canEditCosts && <>Add and edit cost lines.</>,
    user?.canManageRequirements && <>Manage the required-documents checklist on a case.</>,
    user?.canManageAdminConfig && <>Configure the app from the <strong>Admin Panel</strong>.</>,
    user?.canAssignTags && <>Add and remove tags on cases.</>,
    user?.canCreateTags && <>Create new tags in the shared vocabulary (from a case or the Admin Panel).</>,
    user?.canImpersonate && <>Use <strong>Assume identity</strong> to preview the app as another user (nothing can be saved while assuming).</>,
    !user?.canManageAdminConfig && user?.adminTabs &&
      <>Manage tags &amp; auto-tag rules, cost categories and currencies from the <strong>Admin Panel</strong>.</>,
  ].filter(Boolean);

  return (
    <>
      <KbSection title="Your role">
        <p>
          You are signed in as <strong>{roleLabel}</strong>. Your role decides which features
          the app shows you and which cases you can open — the whole interface, this guide
          included, adapts to it. Features outside your role aren't greyed out; they simply
          don't appear.
        </p>
      </KbSection>

      <KbSection title="What your role can do" delay={1}>
        <p>Right now, your account can:</p>
        <ul className="kb-steps" style={{ listStyleType: 'disc' }}>
          {abilities.map((a, i) => <li key={i}>{a}</li>)}
        </ul>
      </KbSection>

      <KbSection title="Which cases you can see" delay={2}>
        {user?.role === 'ClinicManager' ? (
          <p>
            As a Clinic Manager you see the cases belonging to <strong>your own clinic</strong> —
            or clinics, if you manage more than one. Cases from other clinics don't appear
            anywhere: not in All Cases, not in search, not on the Dashboard.
          </p>
        ) : user?.role === 'HoRO' ? (
          <p>
            As Head of RO you see the cases from every clinic in <strong>your region</strong>.
            Cases from other regions don't appear anywhere: not in All Cases, not in search,
            not on the Dashboard.
          </p>
        ) : (
          <p>
            As <strong>{roleLabel}</strong> you see cases from <strong>every clinic</strong> —
            All Cases, search and the Dashboard all cover the full picture.
          </p>
        )}
        <Callout tone="info">
          This isn't a filter you can switch off — it's simply how your access is set up.
          If a case you expected isn't there, it belongs to a clinic outside your access.
        </Callout>
        <VisibleTo user={user} when={(u) => !!u?.canImpersonate}>
          <Callout tone="brand">
            This article adapts to whoever is reading it — a Clinic Manager sees their own
            clinic-scoped version of this page. Use <strong>Assume identity</strong> to preview
            exactly what any role reads here.
          </Callout>
        </VisibleTo>
      </KbSection>

      <KbSection title="Your name, photo and role" delay={3}>
        <p>
          Your display name and photo come from your <strong>Microsoft work account</strong> —
          if there's no photo, your initials are shown instead. Neither can be edited inside
          the tracker; update your Microsoft profile and it follows. Roles are assigned
          centrally by IT — the app never lets you change your own.
        </p>
      </KbSection>
    </>
  );
}

function PinLockBody({ user }) {
  return (
    <>
      <KbSection title="A PIN for shared screens">
        <p>
          Everyone who uses the tracker sets a personal <strong>6-digit PIN</strong> the first
          time they open it — the app won't go any further until one is saved. The PIN
          protects an unattended screen in clinic: when the session locks, whoever sits down
          next needs <em>your</em> PIN (or has to sign out) to get past it. The PIN is yours,
          not the terminal's — it follows your account on any machine.
        </p>
        <PinLockDemo />
        <VisibleTo user={user} when={(u) => !!u?.isSuperAdmin}>
          <Callout tone="brand">
            As a Super Admin your session never locks automatically — use <strong>Lock now</strong> in
            the user menu when you step away.
          </Callout>
        </VisibleTo>
      </KbSection>

      <KbSection title="Choosing your PIN" delay={1}>
        <StepList steps={[
          <>Exactly <strong>6 digits</strong>, numbers only.</>,
          <>A single repeating digit (000000, 111111, …) is rejected — pick something less guessable.</>,
          <>You type it twice to confirm; a mismatch starts you over.</>,
          <>Your actual PIN is never stored anywhere, so nobody — not even IT — can look it up. If you forget it, it gets reset, not recovered.</>,
        ]} />
      </KbSection>

      <KbSection title="When the screen locks" delay={2}>
        <StepList steps={[
          <>After <strong>15 minutes</strong> without activity — measured across all your open tabs, so working in one keeps the others awake.</>,
          <>When the tab has been <strong>hidden for more than 30 seconds</strong> — switching away and coming back asks for the PIN.</>,
          <>Immediately, with <strong>Lock now</strong> in the user menu.</>,
          <>When you open the app with a PIN already set, it starts locked.</>,
        ]} />
        <p>
          Locking or unlocking one tab does the same to every other tab in that browser. The
          lock screen shows your name and email, takes the PIN from the on-screen keypad or
          your keyboard, and offers <strong>Not me — sign out</strong> for anyone else at the
          machine.
        </p>
      </KbSection>

      <KbSection title="Changing or recovering your PIN" delay={3}>
        <p>
          <strong>Change PIN</strong> in the user menu asks for your current PIN, then the new
          one twice. If you've forgotten it, use <strong>Forgot your PIN?</strong> on the lock
          screen: it opens a ticket with IT — an email to <strong>itsupport@therapieclinic.com</strong> with
          you on CC, set up so IT's reply lands straight in your inbox. The ticket includes
          your name, email, role and clinic so IT can verify you, plus an optional note (say
          if it's urgent). Once IT resets the PIN, the lock screen switches to setup and you
          choose a new one.
        </p>
        <Callout tone="warn">
          Five wrong guesses pause the PIN for 60 seconds, doubling with each further wrong
          attempt up to 30 minutes. The lock screen tells you how many attempts remain — if
          you're not sure, use <strong>Forgot your PIN?</strong> instead of burning them.
        </Callout>
      </KbSection>
    </>
  );
}

function PrivacyModeBody() {
  return (
    <>
      <KbSection title="Frosting photo previews">
        <p>
          Case files can carry photographs. If you are working where someone else can see your
          screen — at a front desk, on a shared device, in a meeting — switch on{' '}
          <strong>Privacy</strong> in the user menu at the bottom of the sidebar. Every photo
          thumbnail on the attachment cards is frosted over with a crossed-eye mark; documents
          and everything else look the same as before.
        </p>
      </KbSection>

      <KbSection title="Seeing a photo anyway" delay={1}>
        <p>
          Frosting only affects the thumbnails. Click a photo to open it in the viewer and it
          shows in full — opening one is a deliberate act. The setting is per device and stays
          on until you switch it off; the eye icon in the menu shows the current state.
        </p>
        <Callout tone="info">
          Privacy mode is a courtesy for shared spaces, not access control. What you can open
          is still decided by your role.
        </Callout>
      </KbSection>
    </>
  );
}

export const GETTING_STARTED_ARTICLES = [
  {
    id: 'welcome',
    title: 'What is the AE Tracker',
    category: 'getting-started',
    summary: 'What the tracker is for, what a case is, and where everything lives.',
    keywords: ['introduction', 'overview', 'adverse event', 'near miss', 'clinic incident', 'case', 'welcome', 'start'],
    visibleTo: () => true,
    Body: WelcomeBody,
  },
  {
    id: 'navigation',
    title: 'Finding your way around',
    category: 'getting-started',
    summary: 'The sidebar, collapsed mode, the mobile drawer and the user menu.',
    keywords: ['sidebar', 'navigation', 'menu', 'collapse', 'mobile', 'drawer', 'sign out', 'keyboard'],
    visibleTo: () => true,
    Body: NavigationBody,
  },
  {
    id: 'your-access',
    title: 'Your role and what you can see',
    category: 'getting-started',
    summary: 'What your role can do, which cases you can see, and why.',
    keywords: ['role', 'permissions', 'access', 'scope', 'clinic', 'region', 'profile', 'photo'],
    visibleTo: () => true,
    Body: YourAccessBody,
  },
  {
    id: 'pin-lock',
    title: 'The session PIN lock',
    category: 'getting-started',
    summary: 'The 6-digit session lock: when it locks, unlocking, and changing or resetting your PIN.',
    keywords: ['pin', 'lock', 'unlock', 'security', 'inactivity', 'forgot', 'session', 'lock now'],
    visibleTo: () => true,
    Body: PinLockBody,
  },
  {
    id: 'privacy-mode',
    title: 'Privacy mode for photo previews',
    category: 'getting-started',
    summary: 'Frost photo thumbnails on case files when others can see your screen; open a photo to view it in full.',
    keywords: ['privacy', 'photo', 'thumbnail', 'blur', 'frost', 'screen', 'shared', 'eye'],
    visibleTo: () => true,
    Body: PrivacyModeBody,
  },
];
